/**
 * API client for Tenant API
 */
import type { ApiError } from '@/types';

export class ApiClientError extends Error {
  status: number;
  code: string;
  details?: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    code: string,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function getApiBaseUrl(): string {
  return import.meta.env.VITE_TENANT_API_URL || '/api';
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  token?: string;
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}${endpoint}`;

  const { body, headers: customHeaders, token, ...restOptions } = options;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...customHeaders,
  };

  const config: RequestInit = {
    ...restOptions,
    headers,
    credentials: 'include', // Include cookies for session auth
  };

  if (body) {
    try {
      config.body = JSON.stringify(body);
    } catch (err) {
      console.error('Failed to stringify body:', body);
      console.error('Body keys:', Object.keys(body as object));
      for (const [key, val] of Object.entries(body as object)) {
        console.error(`  ${key}:`, typeof val, val);
      }
      throw err;
    }
  }

  const response = await fetch(url, config);

  if (!response.ok) {
    let errorData: ApiError;
    try {
      const json = await response.json();
      // Backend may return {error: "..."} or {message: "...", code: "..."}
      errorData = {
        code: json.code || 'UNKNOWN_ERROR',
        message: json.message || json.error || response.statusText || 'An unknown error occurred',
        details: json.details,
      };
    } catch {
      errorData = {
        code: 'UNKNOWN_ERROR',
        message: response.statusText || 'An unknown error occurred',
      };
    }
    throw new ApiClientError(
      errorData.message,
      response.status,
      errorData.code,
      errorData.details
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

export const apiClient = {
  get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'GET' });
  },

  post<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'POST', body });
  },

  put<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'PUT', body });
  },

  patch<T>(
    endpoint: string,
    body?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'PATCH', body });
  },

  delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'DELETE' });
  },
};

/**
 * Create an authenticated fetcher for SWR
 */
export function createAuthenticatedFetcher(token: string | undefined) {
  return async <T>(url: string): Promise<T> => {
    if (!token) {
      throw new ApiClientError('No authentication token', 401, 'UNAUTHORIZED');
    }
    return apiClient.get<T>(url, { token });
  };
}
